import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Maximize2, 
  Minimize2, 
  RotateCcw, 
  ArrowLeft,
  X,
  ExternalLink,
  Sparkles
} from 'lucide-react';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const ELaering = () => {
  useDocumentMetadata({
    title: 'E-læring: Medicinteknologier i kommunen | MedicCare',
    description: 'Interaktivt e-læringsmodul om medicinteknologien TIM i kommunen. Gennemfør modulet direkte med videoer, simulationer og faglige cases.'
  });

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef(null);

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen().catch(() => {
          setIsFullscreen(!isFullscreen);
        });
      } else {
        setIsFullscreen(!isFullscreen);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {
          setIsFullscreen(false);
        });
      } else {
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleReload = () => {
    setIsLoading(true);
    setIframeKey((prev) => prev + 1);
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 w-screen h-screen flex flex-col bg-[#1e2318] text-white overflow-hidden select-none"
    >
      {/* Sleek Top Navigation Bar */}
      <header className="h-14 bg-[#2c3223] border-b border-white/10 px-4 sm:px-6 flex items-center justify-between flex-shrink-0 z-20">
        {/* Left: Branding & Back Navigation */}
        <div className="flex items-center gap-3 sm:gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/80 hover:text-white transition-colors group"
            title="Gå til MedicCare.dk forside"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            <span className="hidden sm:inline">MedicCare.dk</span>
          </Link>

          <span className="h-4 w-px bg-white/20 hidden sm:block" />

          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <h1 className="text-xs sm:text-sm font-bold tracking-wide text-white truncate max-w-[200px] sm:max-w-md">
              Medicinteknologier i kommunen
            </h1>
            <span className="hidden md:inline-flex items-center gap-1 text-[11px] font-medium text-emerald-300/90 bg-emerald-950/60 border border-emerald-500/20 px-2 py-0.5 rounded-md">
              <Sparkles className="w-3 h-3" />
              E-læring
            </span>
          </div>
        </div>

        {/* Right: Controls & Actions */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={handleReload}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-slate-200 hover:text-white text-xs font-medium transition-colors cursor-pointer"
            title="Genindlæs e-læringsmodulet"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Genindlæs</span>
          </button>

          <button
            onClick={toggleFullscreen}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-slate-200 hover:text-white text-xs font-medium transition-colors cursor-pointer"
            title={isFullscreen ? 'Forlad fuld skærm' : 'Åbn i fuld skærm'}
          >
            {isFullscreen ? (
              <>
                <Minimize2 className="w-3.5 h-3.5 text-emerald-300" />
                <span className="hidden sm:inline">Minimer</span>
              </>
            ) : (
              <>
                <Maximize2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Fuld skærm</span>
              </>
            )}
          </button>

          <button
            onClick={handleClose}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 hover:text-red-100 text-xs font-medium transition-colors border border-red-500/20 cursor-pointer ml-1"
            title="Luk fane / Afslut"
          >
            <X className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Luk fane</span>
          </button>
        </div>
      </header>

      {/* Main Interactive Viewer Frame */}
      <main className="flex-1 w-full h-full relative bg-white overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 bg-[#f8fafc] flex flex-col items-center justify-center z-10 p-6 text-center">
            <div className="w-12 h-12 border-3 border-[#2c3223] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm font-bold text-slate-800 tracking-wide">
              Indlæser e-læringsmodul...
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Forbereder interaktive videoer, cases og quizzer
            </p>
          </div>
        )}

        <iframe
          key={iframeKey}
          src="/content/index.html"
          title="Medicinteknologier i kommunen - E-læring"
          onLoad={() => setIsLoading(false)}
          className="w-full h-full border-0 block"
          allow="fullscreen; autoplay; clipboard-write; encrypted-media"
        />
      </main>
    </div>
  );
};

export default ELaering;
